/* eslint-disable react/prop-types */
import React, { useState, useCallback, useEffect, forwardRef } from 'react';

export function createGeneralHandlers(settings) {
  return (Component) => {
    const GeneralHandlersWrapper = forwardRef((props, ref) => {
      const { children } = props;
      const [isOpen, setOpen] = useState(false);

      const closeOnClick = useCallback(
        (e) => {
          const wrapper = ref?.current;

          if (props.closeOnClick) {
            return props.closeOnClick(e, props, wrapper);
          }

          if (settings.closeOnClick) {
            return settings.closeOnClick(e, props, wrapper);
          }

          return true;
        },
        [props, settings, ref]
      );

      const toggle = useCallback(() => {
        setOpen((prev) => !prev);
      }, []);

      const close = useCallback(() => {
        setOpen(false);
      }, []);

      const open = useCallback(() => {
        setOpen(true);
      }, []);

      const outsideClickHandler = useCallback(
        (e) => {
          if (!isOpen) return;
          if (!closeOnClick(e)) return;
          toggle();
        },
        [isOpen, closeOnClick, toggle]
      );

      const onEsc = useCallback(
        (event) => {
          if (!isOpen) return;
          if (event.keyCode !== 27) return;
          toggle();
        },
        [isOpen, toggle]
      );

      useEffect(() => {
        document.addEventListener('keydown', onEsc, true);
        document.addEventListener('mousedown', outsideClickHandler, true);

        return () => {
          document.removeEventListener('keydown', onEsc, true);
          document.removeEventListener('mousedown', outsideClickHandler, true);
        };
      }, [onEsc, outsideClickHandler]);

      return (
        <Component
          ref={ref}
          {...props}
          isOpen={isOpen}
          setOpen={setOpen}
          open={open}
          close={close}
          toggle={toggle}
          closeOnClick={closeOnClick}>
          {children}
        </Component>
      );
    });

    // ✅ Set the display name for better debugging
    GeneralHandlersWrapper.displayName = `withGeneralHandlers(${Component.displayName || Component.name || 'Component'})`;

    return GeneralHandlersWrapper;
  };
}

export default function withOpenCloseStuff(settings = {}) {
  return createGeneralHandlers(settings);
}
